/**
 * apiClient.js — single axios wrapper that all NEW page code should use.
 *
 * Why
 * ───
 * Every page in the codebase repeats this boilerplate:
 *
 *     const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('cop31_ultra_token') || localStorage.getItem('cop31_token')}` } });
 *     try { const res = await axios.get(...); ... }
 *     catch (e) { toast.error('something'); }
 *
 * That's:
 *   - 4 token names hard-coded everywhere
 *   - inconsistent error toasts
 *   - no shared loading state
 *   - new contributors copy-paste the wrong token name half the time
 *
 * What this file does
 * ───────────────────
 *   - Picks the right token automatically based on `window.location.pathname`
 *     (same rule the global 401 interceptor in `App.js` uses).
 *   - Returns a thin axios instance (`apiClient`) with `baseURL` already set
 *     to `${REACT_APP_BACKEND_URL}/api`.
 *   - Exposes `getAuthHeaders()` for the rare case a page calls `axios`
 *     directly (e.g., file uploads with custom progress handlers).
 *
 * What it does NOT do
 * ───────────────────
 *   - It does NOT replace the existing `axios.interceptors.response.use(...)`
 *     in `App.js` — that hook handles 401 → logout + redirect globally,
 *     so this wrapper inherits it for free.
 *   - It does NOT touch existing `api/customerApi.js` / `api/supplierApi.js`
 *     etc. Those modules keep working as-is. Migrate at your own pace.
 */
import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Token selection ─────────────────────────────────────────────────────────
// Match the same path → token logic the App.js 401 interceptor uses.
// When called from outside the browser (e.g., SSR / tests) we fall back to
// the customer token because that's the most permissive default.

/**
 * Pick the most appropriate JWT for the current page based on the URL path.
 * Returns `null` when no token is present, so callers can guard accordingly.
 */
export function getToken() {
  if (typeof window === 'undefined') return null;
  const path = window.location.pathname || '';

  if (path.startsWith('/ultra-admin')) {
    return localStorage.getItem('cop31_ultra_token') || null;
  }
  if (path.startsWith('/tedarikci-admin')) {
    return localStorage.getItem('cop31_supplier_token') || null;
  }
  if (path.startsWith('/yuklenci-admin')) {
    return localStorage.getItem('cop31_contractor_token') || null;
  }
  // Customer / public / login pages all use the regular cop31_token.
  // Fallback chain mirrors the legacy `api/customerApi.js` behaviour.
  return (
    localStorage.getItem('cop31_token') ||
    localStorage.getItem('auth_token') ||
    localStorage.getItem('token') ||
    null
  );
}

/** Return the canonical `Authorization` header object, or `{}` when absent. */
export function getAuthHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── apiClient — facade over the default axios singleton ────────────────────
//
// Why the default axios singleton, not `axios.create()`?
// ─────────────────────────────────────────────────────
// `App.js` registers a *response* interceptor on `axios.interceptors.response`
// that handles 401 → token cleanup + login redirect, with subtle path-aware
// logic per role (ultra/supplier/contractor/customer). `axios.create()`
// instances do **not** inherit response interceptors from the default
// singleton, which would silently break auto-logout for every page migrated
// to a custom instance.
//
// To keep that hard-won 401 logic working we go the other way: the facade
// just calls `axios(...)` directly with the absolute URL + injected auth
// header. Existing pages that still call `axios` themselves keep working
// unchanged — both code paths converge on the same response interceptor.

function buildConfig(config = {}) {
  const headers = { ...(config.headers || {}) };
  if (!headers.Authorization) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  return { ...config, headers };
}

function buildUrl(path) {
  if (typeof path !== 'string') return path;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  return `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
}

export const apiClient = {
  get:    (path, config = {})        => axios.get(buildUrl(path), buildConfig(config)),
  post:   (path, body, config = {})  => axios.post(buildUrl(path), body, buildConfig(config)),
  put:    (path, body, config = {})  => axios.put(buildUrl(path), body, buildConfig(config)),
  patch:  (path, body, config = {})  => axios.patch(buildUrl(path), body, buildConfig(config)),
  delete: (path, config = {})        => axios.delete(buildUrl(path), buildConfig(config)),
  /**
   * Generic escape hatch for `useApi`. Mirrors `axios.request(...)`'s shape
   * but resolves the URL relative to `/api` if it starts with `/`.
   */
  request: (config = {}) => axios.request({
    ...buildConfig(config),
    url: buildUrl(config.url),
  }),
};
